import { EncoderDecoder } from '@iotize/common/converter/api';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TapDataError } from '../tap-data-error';
import {
  EditableDataStreamInterface,
  KeyTypeType,
} from '../utility/editable-data-stream';
import { AbstractVariable } from './abstract-variable-stream';

/**
 * You should use ConverterVariableView instead
 */
export class VariableView<DataType, KeyType extends KeyTypeType = KeyTypeType>
  // extends AbstractEditableDataStream<DataType, Uint8Array, KeyType>
  implements EditableDataStreamInterface<DataType, Uint8Array, KeyType>
{
  get converter() {
    return this.context.converter;
  }

  get indexes() {
    return this.context.indexes;
  }

  get rawValues() {
    return this.context.variable.rawValues.pipe(
      map((rawData: Uint8Array) => {
        return this.subDataView(rawData);
      }),
    );
  }

  get valueSnapshot(): DataType | undefined {
    const lastRawValue = this.rawValueSnaphot;
    return lastRawValue !== undefined
      ? this.decodeValue(this.subDataView(lastRawValue))
      : undefined;
  }

  get values(): Observable<DataType> {
    return this.rawValues.pipe(map((v) => this.decodeValue(v)));
  }

  get rawValueSnaphot() {
    const parentValueSnapshot = this.context.variable.rawValueSnaphot;
    return parentValueSnapshot !== undefined
      ? this.subDataView(parentValueSnapshot)
      : parentValueSnapshot;
  }

  constructor(
    public readonly id: KeyType,
    public context: {
      /**
       * The underlying variable from whiich the view will be created
       */
      variable: AbstractVariable<any, any>;
      /**
       * Index to extract from the whole buffer (starting from 0)
       * Buffer size will be the array length * size of one element defined by the variable type
       */
      indexes: number[];
      /**
       * If set to false, Tap will read the current value before editing and writing the new index
       * If set to true and if Tap already know the current value, it will not perform a read before editing the value
       */
      useLastKnownValueBeforeWrite?: boolean;
      /**
       * Defined how the extracted value should be decoded
       */
      converter: EncoderDecoder<DataType, Uint8Array>;
    },
  ) {}

  notifyRawValue(newValue: Uint8Array): void {}

  async read(): Promise<DataType> {
    return this.decodeValue(await this.readRaw());
  }

  write(value: DataType): Promise<any> {
    return this.writeRaw(this.encodeValue(value));
  }

  async readRaw(): Promise<Uint8Array> {
    const rawData = await this.context.variable.readRaw();
    return this.subDataView(rawData);
  }

  async writeRaw(value: Uint8Array): Promise<void> {
    let existingData: Uint8Array;
    if (this.context.useLastKnownValueBeforeWrite) {
      existingData =
        this.context.variable.rawValueSnaphot ||
        (await this.context.variable.readRaw());
    } else {
      existingData = await this.context.variable.readRaw();
    }
    this.indexes.forEach((subIndex, index) => {
      existingData[subIndex] = value[index];
    });

    await this.context.variable.writeRaw(existingData);
  }

  private subDataView(rawData: Uint8Array): Uint8Array {
    return this.indexes.reduce((acc, subIndex, index) => {
      acc[index] = rawData[subIndex];
      return acc;
    }, new Uint8Array(this.indexes.length));
  }

  private encodeValue(value: DataType): Uint8Array {
    try {
      return this.converter.encode(value);
    } catch (err) {
      throw TapDataError.unexpectedEncodedValueError(this, value, err);
    }
  }

  private decodeValue(data: Uint8Array) {
    try {
      return this.converter.decode(data);
    } catch (err) {
      throw TapDataError.unexpectedVariableDataRead(this, data, err);
    }
  }
}
